import { useState, useEffect } from 'react';
import { getReferralsByUsername, getUserByPhone } from '../../../../utils/sharedStorage';

const COUNTRY_NAMES: Record<string, string> = {
  'CI': 'Côte d\'Ivoire',
  'SN': 'Sénégal',
  'BJ': 'Bénin',
  'ML': 'Mali',
  'TG': 'Togo',
  'BF': 'Burkina Faso',
  'GA': 'Gabon',
  'CM': 'Cameroun',
  'CG': 'Congo Brazzaville',
  'CD': 'Congo Kinshasa',
  'GH': 'Ghana'
};

interface TeamMember {
  name: string;
  username: string;
  level: number;
  date: string;
  status: 'active' | 'pending';
  earnings: number;
}

export default function Profil() {
  const [userData, setUserData] = useState({
    username: '',
    name: '',
    phone: '',
    country: 'CI',
    countryName: 'Côte d\'Ivoire',
    registrationDate: '',
    inviteCode: '',
    inviteLink: '',
    balance: 0,
    points: 0,
    totalWithdrawals: 0,
    referrals: {
      level1: 0,
      level2: 0,
      level3: 0
    },
    // ✅ Nouveaux champs pour les gains détaillés
    gainsParrainage: 0,
    gainsTaches: 0,
    gainsPoints: 0,
    gainsTotal: 0
  });

  const [team, setTeam] = useState<TeamMember[]>([]);
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    loadUserDataAndReferrals();
  }, []);

  const loadUserDataAndReferrals = async () => {
    setIsLoading(true);
    try {
      const user = localStorage.getItem('currentUser');
      if (!user) {
        setIsLoading(false);
        return;
      }

      const parsedUser = JSON.parse(user);
      const username = parsedUser.username || parsedUser.name || '';
      
      // ✅ Récupérer les données utilisateur depuis Supabase pour avoir les compteurs à jour
      const freshUserData = await getUserByPhone(parsedUser.phone);

      // ✅ Calculer les gains depuis localStorage (userProfits)
      const profits: Array<{ amount: number; type: string }> = JSON.parse(localStorage.getItem('userProfits') || '[]');
      
      const gainsTaches = profits
        .filter(p => p.type.toLowerCase().includes('tâche') || p.type.toLowerCase().includes('tache'))
        .reduce((sum, p) => sum + p.amount, 0);
      
      const gainsPoints = profits
        .filter(p => p.type.toLowerCase().includes('points') || p.type.toLowerCase().includes('contacts') || p.type.toLowerCase().includes('roue'))
        .reduce((sum, p) => sum + p.amount, 0);

      // ✅ Les gains de parrainage seront calculés depuis Supabase (voir plus bas)
      let gainsParrainage = 0;
      
      if (freshUserData) {
        const savedDomain = localStorage.getItem('referralDomain') || window.location.origin;
        setUserData({
          username,
          name: freshUserData.name || '',
          phone: freshUserData.phone || '',
          country: freshUserData.country || 'CI',
          countryName: COUNTRY_NAMES[freshUserData.country || 'CI'] || 'Côte d\'Ivoire',
          registrationDate: freshUserData.registrationDate ? new Date(freshUserData.registrationDate).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR'),
          inviteCode: username,
          inviteLink: `${savedDomain}/inscription?ref=${username}`,
          balance: freshUserData.balance || 0,
          points: freshUserData.points || 0,
          totalWithdrawals: freshUserData.totalWithdrawals || 0,
          referrals: {
            level1: freshUserData.level1Referrals || 0,
            level2: freshUserData.level2Referrals || 0,
            level3: freshUserData.level3Referrals || 0
          },
          gainsParrainage: 0, // Sera mis à jour après calcul
          gainsTaches,
          gainsPoints,
          gainsTotal: 0 // Sera mis à jour après calcul
        });
      } else {
        // Fallback sur les données locales
        const savedDomain = localStorage.getItem('referralDomain') || window.location.origin;
        setUserData({
          username,
          name: parsedUser.name || '',
          phone: parsedUser.phone || '',
          country: parsedUser.country || 'CI',
          countryName: COUNTRY_NAMES[parsedUser.country || 'CI'] || 'Côte d\'Ivoire',
          registrationDate: parsedUser.registrationDate || new Date().toLocaleDateString('fr-FR'),
          inviteCode: username,
          inviteLink: `${savedDomain}/inscription?ref=${username}`,
          balance: parsedUser.balance || 0,
          points: parsedUser.points || 0,
          totalWithdrawals: parsedUser.totalWithdrawals || 0,
          referrals: {
            level1: parsedUser.level1Referrals || 0,
            level2: parsedUser.level2Referrals || 0,
            level3: parsedUser.level3Referrals || 0
          },
          gainsParrainage: 0,
          gainsTaches,
          gainsPoints,
          gainsTotal: 0
        });
      }

      // ✅ Récupérer les filleuls niveau 1 depuis Supabase
      const level1Referrals = await getReferralsByUsername(username);
      
      const teamMembers: TeamMember[] = [];
      let totalEarnings = 0;

      // ✅ Traiter les filleuls niveau 1
      for (const referral of level1Referrals) {
        const commission = referral.isActivated ? 1500 : 0;
        if (referral.isActivated) totalEarnings += commission;

        teamMembers.push({
          name: referral.name,
          username: referral.username,
          level: 1,
          date: referral.registrationDate ? new Date(referral.registrationDate).toLocaleDateString('fr-FR') : '—',
          status: referral.isActivated ? 'active' : 'pending',
          earnings: commission
        });

        // ✅ Récupérer les filleuls niveau 2 (filleuls des filleuls)
        if (referral.username) {
          const level2Referrals = await getReferralsByUsername(referral.username);
          
          for (const level2 of level2Referrals) {
            const commission2 = level2.isActivated ? 600 : 0;
            if (level2.isActivated) totalEarnings += commission2;

            teamMembers.push({
              name: level2.name,
              username: level2.username,
              level: 2,
              date: level2.registrationDate ? new Date(level2.registrationDate).toLocaleDateString('fr-FR') : '—',
              status: level2.isActivated ? 'active' : 'pending',
              earnings: commission2
            });

            // ✅ Récupérer les filleuls niveau 3
            if (level2.username) {
              const level3Referrals = await getReferralsByUsername(level2.username);
              
              for (const level3 of level3Referrals) {
                const commission3 = level3.isActivated ? 400 : 0;
                if (level3.isActivated) totalEarnings += commission3;

                teamMembers.push({
                  name: level3.name,
                  username: level3.username,
                  level: 3,
                  date: level3.registrationDate ? new Date(level3.registrationDate).toLocaleDateString('fr-FR') : '—',
                  status: level3.isActivated ? 'active' : 'pending',
                  earnings: commission3
                });
              }
            }
          }
        }
      }

      // ✅ Trier par date (plus récent en premier)
      teamMembers.sort((a, b) => {
        const dateA = new Date(a.date.split('/').reverse().join('-')).getTime();
        const dateB = new Date(b.date.split('/').reverse().join('-')).getTime();
        return dateB - dateA;
      });

      setTeam(teamMembers);
      setTotalCommissions(totalEarnings);
      gainsParrainage = totalEarnings;

      // ✅ Mettre à jour les gains totaux
      const gainsTotal = gainsParrainage + gainsTaches + gainsPoints;
      setUserData(prev => ({
        ...prev,
        gainsParrainage,
        gainsTotal
      }));

    } catch (error) {
      console.error('Erreur lors du chargement des filleuls:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Copie échouée', err);
    }
    document.body.removeChild(textarea);
  };

  const handleCopyCode = () => {
    copyToClipboard(userData.inviteCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleCopyLink = () => {
    copyToClipboard(userData.inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`🚀 Rejoins SUCCESSLINK AGENCY avec mon code *${userData.inviteCode}* !\n👉 ${userData.inviteLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleShareFacebook = () => {
    const url = encodeURIComponent(userData.inviteLink);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const handleShareInstagram = () => {
    const fullText = `${userData.inviteCode} — ${userData.inviteLink}`;
    copyToClipboard(fullText);
    window.open('https://www.instagram.com/', '_blank');
  };

  const getLevelBadgeColor = (level: number) => {
    if (level === 1) return 'bg-blue-100 text-blue-700';
    if (level === 2) return 'bg-green-100 text-green-700';
    return 'bg-purple-100 text-purple-700';
  };

  return (
    <div className="space-y-6">
      {/* Indicateur de chargement */}
      {isLoading && (
        <div className="fixed top-4 right-4 z-50 bg-teal-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>
          <span className="text-sm font-semibold">Chargement des filleuls...</span>
        </div>
      )}

      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-4">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-user-line text-4xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold">{userData.name}</h2>
            <p className="text-teal-100">@{userData.username}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Informations personnelles</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Nom complet</span>
              <span className="text-sm font-semibold text-gray-800">{userData.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Nom d'utilisateur</span>
              <span className="text-sm font-semibold text-gray-800">{userData.username}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Téléphone</span>
              <span className="text-sm font-semibold text-gray-800">{userData.phone}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Pays</span>
              <span className="text-sm font-semibold text-gray-800">{userData.countryName}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-600">Date d'inscription</span>
              <span className="text-sm font-semibold text-gray-800">{userData.registrationDate}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Statistiques</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Solde actuel</span>
              <span className="text-sm font-semibold text-green-600">{userData.balance.toLocaleString()} F</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Points totaux</span>
              <span className="text-sm font-semibold text-blue-600">{userData.points.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Total retraits</span>
              <span className="text-sm font-semibold text-orange-600">{userData.totalWithdrawals.toLocaleString()} F</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-sm text-gray-600">Commissions parrainage</span>
              <span className="text-sm font-semibold text-teal-600">{totalCommissions.toLocaleString()} F</span>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ NOUVELLE SECTION : GAINS DÉTAILLÉS */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 border-2 border-green-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-money-dollar-circle-line text-green-600 text-xl"></i>
          Mes gains détaillés
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Gains Parrainage</span>
              <i className="ri-user-add-line text-xl text-orange-500"></i>
            </div>
            <p className="text-2xl font-extrabold text-orange-600">{userData.gainsParrainage.toLocaleString()} F</p>
            <p className="text-xs text-gray-500 mt-1">Commissions niveaux 1, 2 et 3</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Gains Tâches</span>
              <i className="ri-task-line text-xl text-blue-500"></i>
            </div>
            <p className="text-2xl font-extrabold text-blue-600">{userData.gainsTaches.toLocaleString()} F</p>
            <p className="text-xs text-gray-500 mt-1">Tâches quotidiennes accomplies</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Gains Points & Bonus</span>
              <i className="ri-star-line text-xl text-yellow-500"></i>
            </div>
            <p className="text-2xl font-extrabold text-yellow-600">{userData.gainsPoints.toLocaleString()} F</p>
            <p className="text-xs text-gray-500 mt-1">Points, contacts, roue de la chance</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-4 text-white shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Total des gains</span>
              <i className="ri-trophy-line text-xl"></i>
            </div>
            <p className="text-2xl font-extrabold">{userData.gainsTotal.toLocaleString()} F</p>
            <p className="text-xs opacity-90 mt-1">Tous vos revenus cumulés</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-user-add-line text-teal-500 text-xl"></i>
          Parrainage — Invitez vos proches
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">🎫 Votre code d'invitation</label>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 border-2 border-teal-300 rounded-lg bg-gradient-to-r from-teal-50 to-cyan-50 flex items-center">
                <span className="text-xl font-extrabold text-teal-600 tracking-widest">{userData.inviteCode || '—'}</span>
              </div>
              <button
                onClick={handleCopyCode}
                className="px-6 py-3 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className={copiedCode ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copiedCode ? 'Copié !' : 'Copier'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Ce code est votre nom d'utilisateur — partagez-le avec vos filleuls</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">🔗 Lien d'invitation complet</label>
            <div className="flex flex-col gap-2 border border-gray-200 rounded-lg bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="bg-teal-500 text-white font-extrabold text-base px-4 py-1.5 rounded-lg tracking-widest whitespace-nowrap shadow">
                  {userData.inviteCode || '—'}
                </span>
                <span className="text-gray-400 font-bold">—</span>
                <span className="text-xs font-mono text-gray-700 break-all">{userData.inviteLink}</span>
              </div>
              <button
                onClick={handleCopyLink}
                className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm"
              >
                <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {copied ? '✅ Lien copié !' : 'Copier le lien d\'invitation'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1 text-center">Seul le lien est copié — partagez-le avec vos filleuls</p>
          </div>

          {/* ===== BOUTONS DE PARTAGE RÉSEAUX SOCIAUX ===== */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">📲 Partager directement sur</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={handleShareWhatsApp}
                className="flex flex-col items-center justify-center gap-1.5 bg-green-500 hover:bg-green-400 text-white font-bold py-3 rounded-xl transition shadow cursor-pointer whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-2xl"></i>
                </div>
                <span className="text-xs">WhatsApp</span>
              </button>
              <button
                onClick={handleShareFacebook}
                className="flex flex-col items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl transition shadow cursor-pointer whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-facebook-circle-line text-2xl"></i>
                </div>
                <span className="text-xs">Facebook</span>
              </button>
              <button
                onClick={handleShareInstagram}
                className="flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 hover:opacity-90 text-white font-bold py-3 rounded-xl transition shadow cursor-pointer whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-instagram-line text-2xl"></i>
                </div>
                <span className="text-xs">Instagram</span>
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 text-center">Instagram : le lien est copié, collez-le dans votre bio ou story</p>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm font-semibold text-blue-800 mb-2">💰 Commissions de parrainage</p>
            <div className="flex justify-around text-center">
              <div>
                <p className="text-xs text-blue-600">Niveau 1</p>
                <p className="text-lg font-bold text-blue-800">1 500 F</p>
              </div>
              <div>
                <p className="text-xs text-blue-600">Niveau 2</p>
                <p className="text-lg font-bold text-blue-800">600 F</p>
              </div>
              <div>
                <p className="text-xs text-blue-600">Niveau 3</p>
                <p className="text-lg font-bold text-blue-800">400 F</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Statistiques de parrainage</h3>
          <button
            onClick={loadUserDataAndReferrals}
            disabled={isLoading}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-700">Niveau 1</span>
              <i className="ri-user-line text-xl text-blue-600"></i>
            </div>
            <p className="text-2xl font-bold text-blue-800">{userData.referrals.level1}</p>
            <p className="text-xs text-blue-600 mt-1">Filleuls directs</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-green-700">Niveau 2</span>
              <i className="ri-user-2-line text-xl text-green-600"></i>
            </div>
            <p className="text-2xl font-bold text-green-800">{userData.referrals.level2}</p>
            <p className="text-xs text-green-600 mt-1">Filleuls indirects</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-purple-700">Niveau 3</span>
              <i className="ri-user-3-line text-xl text-purple-600"></i>
            </div>
            <p className="text-2xl font-bold text-purple-800">{userData.referrals.level3}</p>
            <p className="text-xs text-purple-600 mt-1">Filleuls niveau 3</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">Mon équipe</h3>
          <p className="text-sm text-gray-500 mt-1">
            {team.length > 0 ? `${team.length} personne${team.length > 1 ? 's' : ''} parrainée${team.length > 1 ? 's' : ''} — Total commissions gagnées : ${totalCommissions.toLocaleString()} F` : 'Aucun filleul pour le moment'}
          </p>
        </div>
        {team.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-team-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 font-semibold">Aucun filleul pour le moment</p>
            <p className="text-sm text-gray-400 mt-2">Partagez votre lien d'invitation pour commencer à construire votre équipe</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nom</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Niveau</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Gains</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {team.map((member, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">{member.name}</p>
                        <p className="text-xs text-gray-400">@{member.username}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 ${getLevelBadgeColor(member.level)} rounded-full text-xs font-semibold whitespace-nowrap`}>
                        Niveau {member.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{member.date}</td>
                    <td className="px-6 py-4">
                      {member.status === 'active' ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1 w-fit">
                          <i className="ri-checkbox-circle-fill"></i>
                          Activé
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1 w-fit">
                          <i className="ri-time-line"></i>
                          En attente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-right">
                      {member.earnings > 0 ? (
                        <span className="text-green-600">+{member.earnings.toLocaleString()} F</span>
                      ) : (
                        <span className="text-gray-400">0 F</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}